import {
  GetFilteredSortedArticlesDocument,
  GetFilteredSortedArticlesQuery,
  GetFilteredSortedArticlesQueryVariables,
  GetTotalFilteredArticlesDocument,
  GetTotalFilteredArticlesQueryVariables
} from '@/gql/generated/graphql'
import { useQueryStates } from 'nuqs'
import { useQuery } from '@urql/next'
import {
  articleFilterParsers,
  constructGetFilteredSortedArticlesQueryVariables,
  constructGetTotalFilteredArticlesQueryVariables,
  NUM_ARTICLES_PER_PAGE,
  ValidArticleSorts
} from './utils'
import { useMemo } from 'react'
import { createUrqlNextCacheContext } from '@utils/common'
import { ARTICLES_REVALIDATE_TAG } from '@consts/common'

type UseArticlesSortAndFilterReturn = {
  sortOrder: ValidArticleSorts
  setSortOrder: (newSortOrder: ValidArticleSorts) => void
  categoryFilterValue: string[]
  setCategoryFilterValue: (value: string, selected: boolean) => void
  pageNumber: number
  setPageNumber: (pageNum: number) => void
  pageData?: GetFilteredSortedArticlesQuery
  fetching: boolean
  totalPages: number
  totalItems: number
  clearFilters: () => void
}

export const useArticlesSortAndFilter = ({
  initialGetTotalFilteredArticlesQueryVariables,
  initialGetFilteredSortedArticlesQueryVariables
}: {
  initialGetTotalFilteredArticlesQueryVariables: GetTotalFilteredArticlesQueryVariables
  initialGetFilteredSortedArticlesQueryVariables: GetFilteredSortedArticlesQueryVariables
}): UseArticlesSortAndFilterReturn => {
  const [{ sort: sortOrder, page: pageNumber, categoryFilterValue }, setQueryStates] =
    useQueryStates(articleFilterParsers)

  const setSortOrder = (newSortOrder: ValidArticleSorts) => {
    setQueryStates({
      sort: newSortOrder
    })
  }

  const setPageNumber = (newPageNumber: number) => {
    setQueryStates({
      page: newPageNumber
    })
  }

  const setCategoryFilterValue = (value: string, selected: boolean) => {
    const newValue = selected ? [...categoryFilterValue, value] : categoryFilterValue.filter((v) => v !== value)
    setQueryStates({
      categoryFilterValue: newValue.length ? newValue : null,
      page: null
    })
  }

  const getTotalFilteredArticlesQueryVariables = constructGetTotalFilteredArticlesQueryVariables(categoryFilterValue)
  const isUsingInitialGetTotalFilteredArticlesQueryVariables = useMemo(() => {
    return (
      JSON.stringify(initialGetTotalFilteredArticlesQueryVariables) ===
      JSON.stringify(getTotalFilteredArticlesQueryVariables)
    )
  }, [initialGetTotalFilteredArticlesQueryVariables, getTotalFilteredArticlesQueryVariables])
  const [countRes] = useQuery({
    query: GetTotalFilteredArticlesDocument,
    variables: getTotalFilteredArticlesQueryVariables,
    context: useMemo(() => {
      return {
        suspense: isUsingInitialGetTotalFilteredArticlesQueryVariables,
        ...createUrqlNextCacheContext({
          tags: [ARTICLES_REVALIDATE_TAG],
          requestPolicy: 'cache-first'
        })
      }
    }, [isUsingInitialGetTotalFilteredArticlesQueryVariables])
  })

  const { data: countData, fetching: countFetching } = countRes
  const totalItems = countData?.articles?.data.length || 0

  const totalPages = Math.ceil(totalItems / NUM_ARTICLES_PER_PAGE)

  const getFilteredSortedArticlesQueryVariables = constructGetFilteredSortedArticlesQueryVariables(
    categoryFilterValue,
    sortOrder,
    pageNumber
  )
  const isUsingInitialGetFilteredSortedArticlesQueryVariables = useMemo(() => {
    return (
      JSON.stringify(initialGetFilteredSortedArticlesQueryVariables) ===
      JSON.stringify(getFilteredSortedArticlesQueryVariables)
    )
  }, [initialGetFilteredSortedArticlesQueryVariables, getFilteredSortedArticlesQueryVariables])
  const [res] = useQuery({
    query: GetFilteredSortedArticlesDocument,
    variables: getFilteredSortedArticlesQueryVariables,
    context: useMemo(() => {
      return {
        suspense: isUsingInitialGetFilteredSortedArticlesQueryVariables,
        ...createUrqlNextCacheContext({
          tags: [ARTICLES_REVALIDATE_TAG],
          requestPolicy: 'cache-first'
        })
      }
    }, [isUsingInitialGetFilteredSortedArticlesQueryVariables])
  })

  const { data: pageData, fetching } = res

  const clearFilters = () => {
    const prevSortOrder = sortOrder
    setQueryStates(null)
    setQueryStates({
      sort: prevSortOrder
    })
  }

  return {
    sortOrder,
    setSortOrder,
    categoryFilterValue,
    setCategoryFilterValue,
    pageNumber,
    setPageNumber,
    pageData,
    totalPages,
    totalItems,
    clearFilters,
    fetching: fetching || countFetching
  }
}
